import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.researcher.discovery');
import { OnInit } from '@angular/core';
import { Service } from "src/libs/portal/season/service";

type DiscoveryTerm = {
    text: string;
    type: string;
    interest: string;
    active: boolean;
};

type DiscoveryWeight = {
    key: string;
    label: string;
    value: number;
    tone: string;
    group: 'citation' | 'activity';
    description?: string;
};

type DiscoveryKeyword = {
    text: string;
    count: number;
};

type DiscoveryRow = {
    id: string;
    researcherId: string;
    rank: number;
    name: string;
    nameEng: string;
    org: string;
    matchType: string;
    matchedPaperCount: number;
    totalCitedCount: number;
    firstAuthorCount: number;
    correspondingCount: number;
    leadAuthorCount: number;
    recentPaperCount: number;
    avgCitedPerPaper: number;
    totalPublications: number;
    topics: DiscoveryKeyword[];
    score: number;
    serverScore: number;
    raw: any;
};

@Component({
    selector: 'wiz-page-researcher-discovery',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.researcher.discovery/view.scss */
:host {
  display: block;
}

.researcher-discovery-page {
  --korenet-brand: #4A6FD8;
  --korenet-brand-hover: #3D5DB8;
  --korenet-brand-soft: rgba(74, 111, 216, 0.1);
}

.discovery-researcher-row,
.discovery-researcher-row > td {
  height: 132px;
}

.discovery-range {
  --range-percent: 50%;
  appearance: none;
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--korenet-brand) 0%, var(--korenet-brand) var(--range-percent), #e2e8f0 var(--range-percent), #e2e8f0 100%);
  outline: none;
}

.discovery-range::-webkit-slider-thumb {
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #ffffff;
  background: var(--korenet-brand);
  box-shadow: 0 1px 5px rgba(15, 23, 42, 0.24);
  cursor: pointer;
}

.discovery-range::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  background: var(--korenet-brand);
  box-shadow: 0 1px 5px rgba(15, 23, 42, 0.24);
  cursor: pointer;
}

.discovery-range:focus-visible::-webkit-slider-thumb {
  box-shadow: 0 0 0 4px rgba(74, 111, 216, 0.18);
}

.discovery-range:focus-visible::-moz-range-thumb {
  box-shadow: 0 0 0 4px rgba(74, 111, 216, 0.18);
}`],
})
export class PageResearcherDiscoveryComponent implements OnInit {
    public keyword: string = 'AI';
    public manualTerm: string = '';
    public loading: boolean = false;
    public searched: boolean = false;
    public expandOpen: boolean = true;
    public weightOpen: boolean = false;
    public total: number = 0;
    public cacheHit: boolean = false;
    public cacheStrategy: string = '';
    public searchStrategy: string = '';
    public searchError: string = '';
    public responseKeyword: string = '';
    public searchTab: string = 'keyword';
    public searchTabDropdownOpen: boolean = false;
    public keywordModalRow: DiscoveryRow | null = null;

    public filters: any = {
        yearFrom: '',
        yearTo: '',
        minPapers: 1,
        topN: 20,
    };

    public expandedTerms: DiscoveryTerm[] = [];
    public rows: DiscoveryRow[] = [];
    public recentKeywords: string[] = [];
    public searchTabOptions: any[] = [
        { value: 'researcher', label: '연구자' },
        { value: 'keyword', label: '키워드' },
    ];

    public weights: DiscoveryWeight[] = [
        { key: 'matchedPaperCount', label: '매칭 논문', value: 25, tone: 'blue', group: 'citation' },
        { key: 'totalCitedCount', label: '총 피인용', value: 30, tone: 'emerald', group: 'citation' },
        { key: 'firstAuthorCount', label: '제1저자', value: 10, tone: 'indigo', group: 'activity' },
        {
            key: 'avgCitedPerPaper',
            label: '논문당 피인용',
            value: 25,
            tone: 'cyan',
            group: 'citation',
            description: '논문 수가 적어도 피인용이 높은 연구자를 우대합니다.',
        },
        { key: 'recentPaperCount', label: '최근5년', value: 10, tone: 'amber', group: 'activity' },
    ];
    public weightColumns: DiscoveryWeight[][] = [
        this.weights.filter((weight: DiscoveryWeight) => weight.group === 'citation'),
        this.weights.filter((weight: DiscoveryWeight) => weight.group === 'activity'),
    ];

    private readonly recentKeywordStorageKey: string = 'korenet.researcherDiscovery.recentKeywords';
    private readonly maxRecentKeywords: number = 8;
    private readonly collapsedTopicLimit: number = 3;
    private readonly defaultWeights: number[] = [25, 30, 10, 25, 10];
    private appliedWeightValues: { [key: string]: number } = {
        matchedPaperCount: 25,
        totalCitedCount: 30,
        firstAuthorCount: 10,
        avgCitedPerPaper: 25,
        recentPaperCount: 10,
    };
    private baseRows: DiscoveryRow[] = [];
    private uiRenderQueued: boolean = false;

    constructor(@Inject( Service) public service: Service) { }

    private normalizeSearchTab(tab: string | null): string {
        return tab === 'researcher' ? 'researcher' : 'keyword';
    }

    private openSearchPage(tab: string, query: string) {
        const nextTab = this.normalizeSearchTab(tab);
        const params = new URLSearchParams();
        if (query) params.set('q', query);
        params.set('tab', nextTab);
        if (nextTab === 'keyword') {
            params.set('fuzzy', 'true');
        }
        location.href = `/search?${params.toString()}`;
    }

    public async ngOnInit() {
        await this.service.init();
        this.recentKeywords = this.loadRecentKeywords();
        const params = new URLSearchParams(location.search);
        this.keyword = (params.get('q') || this.keyword).trim() || 'AI';
        this.searchTab = this.normalizeSearchTab(params.get('tab'));
        const urlExpandedTerms = this.normalizeKeywordList(params.get('expanded_keywords'));
        if (urlExpandedTerms.length > 0) {
            this.expandedTerms = this.toDiscoveryTerms(this.uniqueTerms([this.keyword, ...urlExpandedTerms]));
        }
        await this.runSearch();
    }

    public get activeTermCount(): number {
        return this.expandedTerms.filter((term: DiscoveryTerm) => term.active).length;
    }

    public get weightTotal(): number {
        return this.weights.reduce((total: number, weight: DiscoveryWeight) => total + Number(weight.value || 0), 0);
    }

    public get appliedWeightTotal(): number {
        return this.weights.reduce((total: number, weight: DiscoveryWeight) => total + this.appliedWeightByKey(weight.key), 0);
    }

    public get hasWeightChanges(): boolean {
        return this.weights.some((weight: DiscoveryWeight) => Number(weight.value || 0) !== this.appliedWeightByKey(weight.key));
    }

    public previewWeightPercent(key: string): number {
        const total = this.weightTotal;
        if (total <= 0) return 0;
        const weight = this.weights.find((item: DiscoveryWeight) => item.key === key);
        return Math.round((Number(weight?.value || 0) / total) * 1000) / 10;
    }

    public get effectiveTerms(): string[] {
        return this.expandedTerms
            .filter((term: DiscoveryTerm) => term.active)
            .map((term: DiscoveryTerm) => term.text);
    }

    public get visibleRows(): DiscoveryRow[] {
        const minMatches = Math.max(0, Number(this.filters.minPapers || 0));
        const topN = Math.max(1, Number(this.filters.topN || 20));
        return this.rows
            .filter((row: DiscoveryRow) => row.matchedPaperCount >= minMatches)
            .slice(0, topN);
    }

    public get candidateTotal(): number {
        return Math.max(Number(this.total || 0), this.rows.length);
    }

    public get cacheStatusText(): string {
        if (!this.searched || this.loading || this.searchError) return '';
        const strategy = this.cacheStrategy || this.searchStrategy;
        if (this.cacheHit) {
            return strategy ? `공유 캐시 사용 · ${strategy}` : '공유 캐시 사용';
        }
        return strategy ? `캐시 생성/갱신 · ${strategy}` : '캐시 생성/갱신';
    }

    public get selectedSearchTabOption(): any {
        return this.searchTabOptions.find((option: any) => option.value === this.searchTab) || this.searchTabOptions[1];
    }

    public async runSearch(recordRecent: boolean = false) {
        this.keywordModalRow = null;
        const nextKeyword = (this.keyword || '').trim();
        if (!nextKeyword) {
            this.searched = false;
            this.rows = [];
            this.baseRows = [];
            this.total = 0;
            this.searchError = '';
            await this.service.render();
            return;
        }

        this.keyword = nextKeyword;
        if (recordRecent) {
            this.rememberRecentKeyword(nextKeyword);
        }
        this.responseKeyword = nextKeyword;
        this.loading = true;
        this.searched = true;
        this.searchError = '';
        await this.service.render();

        try {
            const data = await this.requestKeywordSearch(nextKeyword);
            this.applySearchResponse(data, nextKeyword);
        } catch (e) {
            this.rows = [];
            this.baseRows = [];
            this.total = 0;
            this.cacheHit = false;
            this.cacheStrategy = '';
            this.searchStrategy = '';
            this.searchError = String(e?.message || e || '검색 결과를 불러오지 못했습니다.');
        }

        this.loading = false;
        await this.service.render();
    }

    public async selectKeyword(keyword: string) {
        this.searchTab = 'keyword';
        this.keyword = keyword;
        await this.runSearch(true);
    }

    public async onHeaderSearch() {
        const query = (this.keyword || '').trim();
        this.searchTabDropdownOpen = false;
        if (this.searchTab === 'researcher') {
            this.openSearchPage('researcher', query);
            return;
        }
        await this.runSearch(true);
    }

    public async toggleSearchTabDropdown(event: Event) {
        event.stopPropagation();
        this.searchTabDropdownOpen = !this.searchTabDropdownOpen;
        await this.service.render();
    }

    public async selectSearchTab(tab: string, event?: Event) {
        if (event) event.stopPropagation();
        this.searchTab = this.normalizeSearchTab(tab);
        this.searchTabDropdownOpen = false;
        await this.service.render();
    }

    public onTopNChange() {
        this.queueUiRender();
    }

    public onFilterChange() {
        this.queueUiRender();
    }

    public toggleExpandPanel() {
        this.expandOpen = !this.expandOpen;
        this.queueUiRender();
    }

    public toggleWeightPanel() {
        this.weightOpen = !this.weightOpen;
        this.queueUiRender();
    }

    public toggleTerm(index: number) {
        const term = this.expandedTerms[index];
        if (!term) return;
        term.active = !term.active;
        this.queueUiRender();
    }

    public removeTerm(index: number, event?: Event) {
        if (event) event.stopPropagation();
        this.expandedTerms.splice(index, 1);
        this.queueUiRender();
    }

    public addManualTerm() {
        const text = (this.manualTerm || '').trim();
        if (!text) return;
        const exists = this.expandedTerms.some((term: DiscoveryTerm) => this.normalizeTerm(term.text) === this.normalizeTerm(text));
        if (!exists) {
            this.expandedTerms.push({ text, type: '직접', interest: '검토', active: true });
        }
        this.manualTerm = '';
        this.queueUiRender();
    }

    public onWeightChange(key: string, event: any) {
        const value = Math.round(Math.max(0, Math.min(100, Number(event?.target?.value || 0))));
        const target = this.weights.find((weight: DiscoveryWeight) => weight.key === key);
        if (!target) return;
        target.value = value;
        this.queueUiRender();
    }

    public async applyWeights() {
        this.syncAppliedWeights();
        this.recomputeScores();
        await this.service.render();
    }

    public resetWeights() {
        this.weights.forEach((weight: DiscoveryWeight, index: number) => {
            weight.value = this.defaultWeights[index];
        });
        this.queueUiRender();
    }

    public termChipClass(term: DiscoveryTerm): string {
        if (!term?.active) return 'border border-slate-200 bg-white text-slate-500';
        if (term.type === '원본') return 'border border-[#4A6FD8]/30 bg-[#EEF2FF] text-[#1E3A8A]';
        if (term.type === '직접') return 'border border-orange-200 bg-orange-50 text-orange-700';
        return 'border border-slate-200 bg-slate-100 text-slate-700';
    }

    public visibleTopics(row: DiscoveryRow): DiscoveryKeyword[] {
        return this.allTopics(row).slice(0, this.collapsedTopicLimit);
    }

    public allTopics(row: DiscoveryRow | null): DiscoveryKeyword[] {
        return row?.topics || [];
    }

    public hiddenTopicCount(row: DiscoveryRow): number {
        return Math.max(0, this.allTopics(row).length - this.collapsedTopicLimit);
    }

    public async openKeywordModal(row: DiscoveryRow, event?: Event) {
        if (event) event.stopPropagation();
        this.keywordModalRow = row;
        await this.service.render();
    }

    public async closeKeywordModal(event?: Event) {
        if (event) event.stopPropagation();
        this.keywordModalRow = null;
        await this.service.render();
    }

    public openResearcher(row: DiscoveryRow) {
        const researcherId = row?.researcherId || row?.id || '';
        if (!researcherId) return;
        const params = new URLSearchParams();
        const keywords = this.uniqueTerms([
            ...this.effectiveTerms,
            ...this.topicTexts(row),
        ]);
        if (keywords.length > 0) {
            params.set('keywords', keywords.join('|'));
        }
        const qs = params.toString();
        const detailUrl = `/researcher/${researcherId}` + (qs ? `?${qs}` : '');
        window.open(detailUrl, '_blank', 'noopener,noreferrer');
    }

    public openLegacyKeywordSearch() {
        const query = (this.keyword || '').trim();
        this.openSearchPage('keyword', query);
    }

    private loadRecentKeywords(): string[] {
        try {
            const stored = JSON.parse(localStorage.getItem(this.recentKeywordStorageKey) || '[]');
            if (!Array.isArray(stored)) return [];
            return this.uniqueTerms(stored.map((item: any) => String(item || '').trim()))
                .slice(0, this.maxRecentKeywords);
        } catch {
            return [];
        }
    }

    private rememberRecentKeyword(keyword: string) {
        const text = String(keyword || '').trim();
        if (!text) return;

        const normalized = this.normalizeTerm(text);
        this.recentKeywords = [
            text,
            ...this.recentKeywords.filter((item: string) => this.normalizeTerm(item) !== normalized),
        ].slice(0, this.maxRecentKeywords);

        try {
            localStorage.setItem(this.recentKeywordStorageKey, JSON.stringify(this.recentKeywords));
        } catch {
            // Storage can be unavailable in privacy-restricted browsers.
        }
    }

    private async requestKeywordSearch(keyword: string): Promise<any> {
        const searchContext = await this.resolveKeywordSearchContext(keyword);
        const params = new URLSearchParams();
        params.set('keyword', searchContext.keyword);
        params.set('page', '1');
        params.set('dump', String(Math.max(200, Number(this.filters.topN || 20))));
        params.set('sort', 'match');
        params.set('order', 'DESC');
        params.set('fuzzy', 'true');
        params.set('cache_reset', 'false');

        if (searchContext.terms.length > 0) {
            params.set('expanded_keywords', JSON.stringify(searchContext.terms));
        }

        const response = await fetch(`/wiz/api/page.search/keyword_search?${params.toString()}`, {
            method: 'POST',
            credentials: 'include',
        });
        const json = await response.json();
        if (!response.ok || json?.code !== 200) {
            throw new Error(json?.data?.message || json?.message || 'keyword_search API failed');
        }
        return json.data || {};
    }

    private async resolveKeywordSearchContext(keyword: string): Promise<{ keyword: string; terms: string[] }> {
        const currentTerms = this.expandedTerms.map((term: DiscoveryTerm) => term.text);
        if (currentTerms.length > 1 && this.termListBelongsToKeyword(currentTerms, keyword)) {
            const activeTerms = this.effectiveTerms;
            return {
                keyword,
                terms: activeTerms.length > 0 ? activeTerms : [keyword],
            };
        }

        const expansion = await this.requestKeywordExpansion(keyword);
        const searchKeyword = expansion.keyword || keyword;
        const terms = this.uniqueTerms([searchKeyword, ...expansion.terms]);
        this.expandedTerms = this.toDiscoveryTerms(terms);
        await this.service.render();

        return {
            keyword: searchKeyword,
            terms: terms.length > 0 ? terms : [searchKeyword],
        };
    }

    private async requestKeywordExpansion(keyword: string): Promise<{ keyword: string; terms: string[] }> {
        const fallback = { keyword, terms: this.uniqueTerms([keyword]) };
        const params = new URLSearchParams();
        params.set('keyword', keyword);

        try {
            const response = await fetch(`/wiz/api/page.search/keyword_expand?${params.toString()}`, {
                method: 'POST',
                credentials: 'include',
            });
            const json = await response.json();
            if (!response.ok || json?.code !== 200) return fallback;

            const data = json.data || {};
            const searchKeyword = String(data?.search_keyword || keyword || '').trim() || keyword;
            const terms = this.normalizeKeywordList(data?.expanded_keywords);
            return {
                keyword: searchKeyword,
                terms: terms.length > 0 ? terms : this.uniqueTerms([searchKeyword]),
            };
        } catch {
            return fallback;
        }
    }

    private applySearchResponse(data: any, fallbackKeyword: string) {
        const searchKeyword = String(data?.search_keyword || fallbackKeyword || '').trim() || fallbackKeyword;
        const responseTerms = this.normalizeKeywordList(data?.expanded_keywords);
        const activeTerms = this.effectiveTerms;
        const terms = this.uniqueTerms([
            searchKeyword,
            ...responseTerms,
            ...activeTerms,
        ]);
        this.responseKeyword = searchKeyword;
        this.keyword = searchKeyword;
        this.expandedTerms = this.toDiscoveryTerms(terms);

        this.total = Number(data?.total || 0) || 0;
        this.cacheHit = !!data?.keyword_result_cache_hit;
        this.cacheStrategy = String(data?.keyword_result_cache_strategy || '');
        this.searchStrategy = String(data?.keyword_search_strategy || '');
        this.baseRows = (data?.rows || []).map((row: any, index: number) => this.mapApiRow(row, index));
        this.recomputeScores();
    }

    private mapApiRow(row: any, index: number): DiscoveryRow {
        const researcherId = String(row?.researcher_id || row?.id || '').trim();
        const hasPrimary = this.hasPrimaryKeywordMetrics(row);
        const topics = this.getMatchedKeywordMetrics(row);
        const fallbackPaperCount = hasPrimary ? this.toNumber(row?.primary_keyword_paper_count) : this.toNumber(row?.keyword_paper_count);
        const matchedPaperCount = this.toNumber(row?.matched_paper_cnt) || fallbackPaperCount;
        const totalCitedCount = this.toNumber(row?.total_cited_count);
        const firstAuthorCount = this.toNumber(row?.first_author_cnt);
        const correspondingCount = this.toNumber(row?.corresponding_cnt);
        const leadAuthorCount = firstAuthorCount + correspondingCount;
        const recentPaperCount = this.toNumber(row?.recent_paper_cnt);
        const avgCitedPerPaper = this.toNumber(row?.avg_cited_per_paper)
            || (matchedPaperCount > 0 ? totalCitedCount / matchedPaperCount : 0);
        const totalPublications = this.toNumber(row?.total_publication_count)
            || this.toNumber(row?.paper_cnt) + this.toNumber(row?.patent_cnt) + this.toNumber(row?.report_cnt);
        const serverScore = this.toNumber(row?.discovery_relevance_score || row?.relevance_score);

        return {
            id: researcherId || `RD-${index + 1}`,
            researcherId,
            rank: index + 1,
            name: String(row?.name_kor || row?.name_eng || researcherId || '-').trim(),
            nameEng: String(row?.name_eng || '').trim(),
            org: String(row?.org_name || row?.org || '').trim(),
            matchType: hasPrimary ? 'primary' : (topics.length > 1 ? 'expanded' : 'keyword'),
            matchedPaperCount,
            totalCitedCount,
            firstAuthorCount,
            correspondingCount,
            leadAuthorCount,
            recentPaperCount,
            avgCitedPerPaper,
            totalPublications,
            topics,
            score: serverScore,
            serverScore,
            raw: row,
        };
    }

    private hasPrimaryKeywordMetrics(row: any): boolean {
        return this.toNumber(row?.primary_keyword_pub_count) > 0
            || this.toNumber(row?.primary_keyword_paper_count) > 0
            || this.toNumber(row?.primary_keyword_patent_count) > 0
            || this.toNumber(row?.primary_keyword_report_count) > 0;
    }

    private recomputeScores() {
        const rows = this.baseRows.map((row: DiscoveryRow) => ({
            ...row,
            topics: row.topics.map((topic: DiscoveryKeyword) => ({ ...topic })),
        }));
        if (!rows.length) {
            this.rows = [];
            return;
        }

        const totals = this.appliedWeightTotal || 1;
        const normalizedWeights = {
            matchedPaperCount: this.appliedWeightByKey('matchedPaperCount') / totals,
            totalCitedCount: this.appliedWeightByKey('totalCitedCount') / totals,
            firstAuthorCount: this.appliedWeightByKey('firstAuthorCount') / totals,
            avgCitedPerPaper: this.appliedWeightByKey('avgCitedPerPaper') / totals,
            recentPaperCount: this.appliedWeightByKey('recentPaperCount') / totals,
        };

        const matchedPaperNorm = this.normalize(rows.map((row: DiscoveryRow) => row.matchedPaperCount));
        const citedNorm = this.normalize(rows.map((row: DiscoveryRow) => row.totalCitedCount));
        const firstAuthorNorm = this.normalize(rows.map((row: DiscoveryRow) => row.firstAuthorCount));
        const avgCitedPerPaperNorm = this.normalize(rows.map((row: DiscoveryRow) => row.avgCitedPerPaper));
        const recentNorm = this.normalize(rows.map((row: DiscoveryRow) => row.recentPaperCount));

        rows.forEach((row: DiscoveryRow, index: number) => {
            const rawScore = normalizedWeights.matchedPaperCount * matchedPaperNorm[index]
                + normalizedWeights.totalCitedCount * citedNorm[index]
                + normalizedWeights.firstAuthorCount * firstAuthorNorm[index]
                + normalizedWeights.avgCitedPerPaper * avgCitedPerPaperNorm[index]
                + normalizedWeights.recentPaperCount * recentNorm[index];
            row.score = Math.round(rawScore * 1000) / 10;
        });

        rows.sort((a: DiscoveryRow, b: DiscoveryRow) => {
            if (b.score !== a.score) return b.score - a.score;
            if (b.matchedPaperCount !== a.matchedPaperCount) return b.matchedPaperCount - a.matchedPaperCount;
            return b.totalCitedCount - a.totalCitedCount;
        });
        rows.forEach((row: DiscoveryRow, index: number) => row.rank = index + 1);
        this.rows = rows;
    }

    private appliedWeightByKey(key: string): number {
        const value = Number(this.appliedWeightValues[key] || 0);
        return Number.isFinite(value) ? value : 0;
    }

    private syncAppliedWeights() {
        this.weights.forEach((weight: DiscoveryWeight) => {
            this.appliedWeightValues[weight.key] = Math.max(0, Math.min(100, Number(weight.value || 0)));
        });
    }

    private normalize(values: number[]): number[] {
        if (!values.length) return [];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const span = max - min || 1;
        return values.map((value: number) => (value - min) / span);
    }

    private normalizeKeywordList(value: any): string[] {
        let source = value;
        if (typeof value === 'string') {
            const text = value.trim();
            if (text.startsWith('[')) {
                try {
                    source = JSON.parse(text);
                } catch {
                    source = text;
                }
            }
        }
        const values = Array.isArray(source) ? source : String(source || '').split(/[;,|]/);
        return this.uniqueTerms(values.map((item: any) => String(item || '').trim()).filter((item: string) => !!item));
    }

    private toDiscoveryTerms(terms: string[]): DiscoveryTerm[] {
        return this.uniqueTerms(terms || []).map((term: string, index: number) => ({
            text: term,
            type: index === 0 ? '원본' : '확장',
            interest: index === 0 ? '핵심' : '확장',
            active: true,
        }));
    }

    private getMatchedKeywordMetrics(row: any): DiscoveryKeyword[] {
        const primaryKeywords = this.normalizeKeywordList(row?.primary_matched_keywords);
        const matchedKeywords = this.normalizeKeywordList(row?.matched_keywords);
        const focusTerms = this.uniqueTerms([
            ...matchedKeywords,
            ...primaryKeywords,
            ...this.effectiveTerms,
            this.responseKeyword || this.keyword,
        ]);
        const detailKeywords = this.getFulltextMatchedDetailKeywords(row, focusTerms);
        const keywords = this.uniqueTerms([
            ...detailKeywords,
            ...matchedKeywords,
            ...primaryKeywords,
        ]);
        const primaryCounts = this.normalizeKeywordCountMap(row?.primary_matched_keyword_counts);
        const matchedCounts = this.normalizeKeywordCountMap(row?.matched_keyword_counts);
        const countMap: { [key: string]: number } = { ...primaryCounts };
        Object.keys(matchedCounts).forEach((key: string) => {
            countMap[key] = Math.max(countMap[key] || 0, matchedCounts[key]);
        });
        const fallbackCount = this.toNumber(row?.keyword_pub_count)
            || this.toNumber(row?.primary_keyword_pub_count);

        return keywords.map((text: string) => {
            const key = this.normalizeTerm(text);
            let count = this.toNumber(countMap[key]);
            if (!count && keywords.length === 1) {
                count = fallbackCount;
            }
            return { text, count };
        });
    }

    private getFulltextMatchedDetailKeywords(row: any, focusTerms: string[]): string[] {
        const keywordKor = this.normalizeKeywordList(row?.keyword_kor);
        const researcherKeywords = keywordKor.length > 0
            ? keywordKor
            : this.normalizeKeywordList(row?.keyword_eng);
        if (researcherKeywords.length === 0 || focusTerms.length === 0) return [];

        return researcherKeywords
            .map((keyword: string, index: number) => ({
                keyword,
                index,
                match: this.getFulltextKeywordMatch(keyword, focusTerms),
            }))
            .filter((item: any) => item.match.priority < 3)
            .sort((a: any, b: any) => {
                if (a.match.priority !== b.match.priority) return a.match.priority - b.match.priority;
                if (a.match.index !== b.match.index) return a.match.index - b.match.index;
                return a.index - b.index;
            })
            .map((item: any) => item.keyword);
    }

    private getFulltextKeywordMatch(keyword: string, focusTerms: string[]): { priority: number; index: number } {
        const keywordKey = this.normalizeTerm(keyword);
        const fallback = { priority: 3, index: Number.MAX_SAFE_INTEGER };
        if (!keywordKey) return fallback;

        for (let index = 0; index < focusTerms.length; index++) {
            if (keywordKey === this.normalizeTerm(focusTerms[index])) {
                return { priority: 0, index };
            }
        }
        for (let index = 0; index < focusTerms.length; index++) {
            const focusKey = this.normalizeTerm(focusTerms[index]);
            if (focusKey && keywordKey.includes(focusKey)) {
                return { priority: 1, index };
            }
        }
        for (let index = 0; index < focusTerms.length; index++) {
            const focusKey = this.normalizeTerm(focusTerms[index]);
            if (focusKey && keywordKey.length >= 2 && focusKey.includes(keywordKey)) {
                return { priority: 2, index };
            }
        }
        return fallback;
    }

    private normalizeKeywordCountMap(value: any): { [key: string]: number } {
        let source = value;
        if (typeof value === 'string') {
            try {
                source = JSON.parse(value);
            } catch {
                source = {};
            }
        }
        const result: { [key: string]: number } = {};
        if (!source || typeof source !== 'object' || Array.isArray(source)) {
            return result;
        }
        Object.keys(source).forEach((keyword: string) => {
            const key = this.normalizeTerm(keyword);
            const count = this.toNumber(source[keyword]);
            if (key && count > 0) {
                result[key] = Math.max(result[key] || 0, count);
            }
        });
        return result;
    }

    private topicTexts(row: DiscoveryRow): string[] {
        return this.allTopics(row)
            .map((topic: DiscoveryKeyword) => String(topic?.text || '').trim())
            .filter((topic: string) => !!topic);
    }

    private uniqueTerms(values: any[]): string[] {
        const seen = new Set<string>();
        const terms: string[] = [];
        (values || []).forEach((value: any) => {
            const text = String(value || '').trim();
            const key = this.normalizeTerm(text);
            if (!key || seen.has(key)) return;
            seen.add(key);
            terms.push(text);
        });
        return terms;
    }

    private termListBelongsToKeyword(terms: string[], keyword: string): boolean {
        const keywordKey = this.normalizeTerm(keyword);
        if (!keywordKey) return false;
        const firstTerm = this.uniqueTerms(terms || [])[0] || '';
        return this.normalizeTerm(firstTerm) === keywordKey;
    }

    private toNumber(value: any): number {
        const number = Number(value || 0);
        return Number.isFinite(number) ? number : 0;
    }

    private normalizeTerm(value: string): string {
        return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
    }

    private queueUiRender() {
        if (this.uiRenderQueued) return;
        this.uiRenderQueued = true;
        const render = async () => {
            this.uiRenderQueued = false;
            await this.service.render();
        };
        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => { render(); });
        } else {
            setTimeout(() => { render(); }, 0);
        }
    }
}

export default PageResearcherDiscoveryComponent;